// AdminSelectorContext.tsx - Global state for SuperAdmin to select which admin to impersonate/filter
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { API_BASE_URL } from '../constants/config';
import { getAndSanitize } from '../shared/utils/sanitization';

interface Admin {
  id: string;
  username: string;
  email?: string;
  active: boolean;
}

interface AdminSelectorContextType {
  selectedAdminId: string | null;
  selectedAdmin: Admin | null;
  admins: Admin[];
  isLoading: boolean;
  isSuperAdmin: boolean;
  setSelectedAdmin: (adminId: string | null) => void;
  clearSelectedAdmin: () => void;
}

const AdminSelectorContext = createContext<AdminSelectorContextType | undefined>(undefined);

const STORAGE_KEY = 'superadmin_selected_admin';

export const AdminSelectorProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const role = getAndSanitize('role');
  const token = getAndSanitize('token');
  const isSuperAdmin = role === 'SUPERADMIN';

  // Initialize from localStorage
  const [selectedAdminId, setSelectedAdminIdState] = useState<string | null>(() => {
    if (!isSuperAdmin) return null;
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored && stored !== 'null' ? stored : null;
  });

  // Fetch admins list (only if SuperAdmin)
  const { data: admins = [], isLoading } = useQuery<Admin[]>({
    queryKey: ['admins-list'],
    queryFn: async () => {
      if (!isSuperAdmin) return [];

      const response = await axios.get(`${API_BASE_URL}/api/auth/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Filter only ADMIN role users
      const users = response.data.data || [];
      return users.filter((user: any) => user.role === 'ADMIN');
    },
    enabled: isSuperAdmin && !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Find currently selected admin object
  const selectedAdmin = admins.find(admin => admin.id === selectedAdminId) || null;

  // Update selected admin
  const setSelectedAdmin = useCallback((adminId: string | null) => {
    if (!isSuperAdmin) {
      console.warn('[AdminSelector] Only SuperAdmin can select admins');
      return;
    }

    setSelectedAdminIdState(adminId);

    // Persist to localStorage
    if (adminId) {
      localStorage.setItem(STORAGE_KEY, adminId);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }

  }, [isSuperAdmin]);

  // Clear selection (view all)
  const clearSelectedAdmin = useCallback(() => {
    setSelectedAdmin(null);
  }, [setSelectedAdmin]);

  // Clear selection if user is not SuperAdmin
  useEffect(() => {
    if (!isSuperAdmin && selectedAdminId) {
      setSelectedAdminIdState(null);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [isSuperAdmin, selectedAdminId]);

  const value: AdminSelectorContextType = {
    selectedAdminId,
    selectedAdmin,
    admins,
    isLoading,
    isSuperAdmin,
    setSelectedAdmin,
    clearSelectedAdmin,
  };

  return (
    <AdminSelectorContext.Provider value={value}>
      {children}
    </AdminSelectorContext.Provider>
  );
};

// Hook to use AdminSelector context
export const useAdminSelector = (): AdminSelectorContextType => {
  const context = useContext(AdminSelectorContext);
  if (!context) {
    throw new Error('useAdminSelector must be used within AdminSelectorProvider');
  }
  return context;
};

// Helper hook to get effective admin ID (for queries)
export const useEffectiveAdminId = (): string | null => {
  const { isSuperAdmin, selectedAdminId } = useAdminSelector();
  const currentUserId = getAndSanitize('userId');

  // If not SuperAdmin, always use own ID
  if (!isSuperAdmin) {
    return currentUserId;
  }

  // If SuperAdmin:
  // - null means "view all"
  // - string means "view specific admin"
  return selectedAdminId;
};
